import { createAsyncThunk } from '@reduxjs/toolkit';

import { blockSlice } from '../block.slice';
import { regionSlice } from '../region.slice';
import { sectionSlice } from '../section.slice';
import { widgetInstanceSlice } from '../widgetInstance.slice';

import { templateActions, templateSelectors } from './template.slice';
import {
  mergeTemplateWithDefaults,
  validateTemplateStructure,
} from './template.utils';

import type { TemplateSnapshot } from './template.types';
import type { RootState } from '../../redux-store';

/**
 * Apply a template to restore its layout configuration
 * This will replace the entire current layout with the template's layout
 */
export const applyTemplate = createAsyncThunk<
  void,
  string,
  { state: RootState; rejectValue: string }
>(
  'template/apply',
  async (templateId, { getState, dispatch, rejectWithValue }) => {
    const state = getState();

    // Get the template
    const template = templateSelectors.selectById(state, templateId);

    if (!template) {
      return rejectWithValue(`Template ${templateId} not found`);
    }

    // Check if we're applying a currently previewed template
    const isCurrentlyPreviewed =
      state.template.previewTemplateId === templateId;

    let layoutToApply: TemplateSnapshot;

    if (isCurrentlyPreviewed) {
      // Use current Redux state (the preview state that user sees and edited)
      layoutToApply = {
        ...template,
        regions: state.regions,
        sections: Object.values(state.sections.entities).filter(
          (s): s is NonNullable<typeof s> => s !== undefined,
        ),
        blocks: state.blocks.entities,
        widgetInstances: state.widgetInstances.entities,
      };

      // If it's a custom template, also save this state back to the template
      if (template.isCustom) {
        dispatch(templateActions.updateTemplate(layoutToApply));
      }
    } else {
      // Use stored template snapshot
      layoutToApply = template;
    }

    // Validate template structure
    if (!validateTemplateStructure(layoutToApply)) {
      return rejectWithValue(`Template ${templateId} has invalid structure`);
    }

    // Merge with defaults for graceful degradation
    const validatedTemplate = mergeTemplateWithDefaults(layoutToApply);

    try {
      // Add a small delay to ensure loading state is visible (better UX)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 1: Restore regions
      dispatch(regionSlice.actions.setRegions(validatedTemplate.regions));

      // Step 2: Restore sections
      dispatch(sectionSlice.actions.setSections(validatedTemplate.sections));

      // Step 3: Restore blocks
      dispatch(blockSlice.actions.setBlocks(validatedTemplate.blocks));

      // Step 4: Restore widget instances
      dispatch(
        widgetInstanceSlice.actions.setWidgetInstances(
          validatedTemplate.widgetInstances,
        ),
      );

      // Step 5: Update active template tracking
      dispatch(
        templateActions.setActiveTemplate({
          id: template.id,
          name: template.name,
          isCustom: template.isCustom,
        }),
      );

      // Step 6: Clear preview state if any
      dispatch(templateActions.clearPreview());
    } catch (error) {
      console.error('Error applying template:', error);
      return rejectWithValue(
        `Failed to apply template: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  },
);

/**
 * Preview a template without marking it as active
 */
export const previewTemplate = createAsyncThunk<
  void,
  string,
  { state: RootState; rejectValue: string }
>(
  'template/preview',
  async (templateId, { getState, dispatch, rejectWithValue }) => {
    const state = getState();

    // Get the template to preview
    const template = templateSelectors.selectById(state, templateId);

    if (!template) {
      return rejectWithValue(`Template ${templateId} not found`);
    }

    // Validate template structure
    if (!validateTemplateStructure(template)) {
      return rejectWithValue(`Template ${templateId} has invalid structure`);
    }

    // Merge with defaults for graceful degradation
    const validatedTemplate = mergeTemplateWithDefaults(template);

    try {
      // Load template data into store (for preview display)
      dispatch(regionSlice.actions.setRegions(validatedTemplate.regions));
      dispatch(sectionSlice.actions.setSections(validatedTemplate.sections));
      dispatch(blockSlice.actions.setBlocks(validatedTemplate.blocks));
      dispatch(
        widgetInstanceSlice.actions.setWidgetInstances(
          validatedTemplate.widgetInstances,
        ),
      );

      // Set preview template ID (marks it as preview, not active)
      dispatch(templateActions.setPreviewTemplate(templateId));
    } catch (error) {
      console.error('Error previewing template:', error);
      return rejectWithValue(
        `Failed to preview template: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  },
);
